import { ReactNode } from 'react';
import AvatarChip from './avatar-chip';
import { documentTypeOptions } from './google-document-types';
import StatusLabel, { StatusType } from './status-label';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps } from '@mui/material/styles';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';

export type TargetType =
  | 'User'
  | 'Group'
  | 'Domain'
  | 'gPanel Role'
  | 'OrgUnit'
  | 'Shared Drive'
  | 'RegEx'
  | 'User Drive'
  | 'Resource Calendar'
  | 'Shared Drive'
  | 'Unknown';

interface AvatarChipItems {
  id?: string;
  email: string;
  type: TargetType;
}
export interface ReviewSectionItem {
  label?: string;
  value: string | number | boolean | string[] | AvatarChipItems[] | ReactNode;
  variant?: string;
  isVisible?: boolean;
  flex?: boolean;
  sx?: SxProps;
}

export interface ReviewSection {
  title: string;
  headerButton?: ReactNode;
  leftWidth?: {
    columnWidth?: number;
    labelWidth?: number;
    valueWidth?: number;
  };
  rightWidth?: {
    columnWidth?: number;
    labelWidth?: number;
    valueWidth?: number;
  };
  leftItems?: ReviewSectionItem[];
  rightItems?: ReviewSectionItem[];
}

interface SharedReviewLayoutItemProps {
  item: ReviewSectionItem;
  hasOpposite: boolean;
  widths?: { labelWidth?: number; valueWidth?: number };
}

export default function SharedReviewLayoutItem({
  item,
  hasOpposite,
  widths,
}: SharedReviewLayoutItemProps) {
  const theme = useTheme();
  const {
    isVisible = true,
    label,
    value,
    variant = 'default',
    flex = false,
  } = item;
  if (!isVisible) return null;
  const gridSizeLabel = hasOpposite
    ? widths?.labelWidth ?? 5
    : widths?.labelWidth ?? 4;
  const gridSizeValue = flex
    ? 12
    : hasOpposite
    ? label
      ? widths?.valueWidth ?? 7
      : 12
    : label
    ? widths?.valueWidth ?? 8
    : 12;

  function displayValue() {
    const textColor = theme.palette.semantic.text.body;
    if (typeof value === 'boolean') {
      return (
        <Typography color={textColor}>
          {value ? <Check /> : <Close />}
        </Typography>
      );
    } else if (
      typeof value === 'string' &&
      ['enabled', 'active', 'disabled', 'draft'].includes(value.toLowerCase())
    ) {
      const lowerValue = value.toUpperCase() as StatusType;
      return lowerValue ? (
        <StatusLabel status={lowerValue} />
      ) : (
        <Typography sx={{ textTransform: 'capitalize', color: textColor }}>
          {value}
        </Typography>
      );
    } else if (typeof value === 'string' || typeof value === 'number') {
      if (variant === 'date' && typeof value === 'number')
        return (
          <Typography
            key={value}
            sx={{ wordBreak: 'break-word', color: textColor }}
          >
            {new Date(value).toLocaleString()}
          </Typography>
        );
      return (
        <Typography
          key={value}
          sx={{ wordBreak: 'break-word', color: textColor }}
        >
          {value}
        </Typography>
      );
    } else if (Array.isArray(value)) {
      if (typeof value[0] === 'string') {
        if (variant === 'fileType') {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 1,
                width: '100%',
              }}
            >
              {value.map((types) => {
                const file = documentTypeOptions.find(
                  (type) => type.value === types
                );
                if (!file) return null;
                return (
                  <Tooltip key={file.displayValue} title={file.displayValue}>
                    <Chip
                      key={file.displayValue}
                      label={file.displayValue}
                      size="small"
                      avatar={<Avatar src={file.icon} />}
                      sx={{
                        width: 'fit-content',
                        backgroundColor: '-moz-initial',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          );
        } else {
          return (
            <Stack direction="column">
              {value
                .filter((val): val is string => typeof val === 'string')
                .map((val) => (
                  <Typography key={val} sx={{ color: textColor }}>
                    {val}
                  </Typography>
                ))}
            </Stack>
          );
        }
      } else if (variant === 'chip') {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1,
              width: '100%',
            }}
          >
            {(value as AvatarChipItems[]).map((val) => (
              <AvatarChip
                key={val.id ? val.id : val.email}
                email={val.email}
                type={val.type as TargetType}
              />
            ))}
          </Box>
        );
      } else return null;
    } else return value as ReactNode;
  }

  return (
    <>
      {label && (
        <Grid
          size={{
            xs: gridSizeLabel,
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: theme.palette.semantic.text.title2 }}
          >
            {label}
          </Typography>
        </Grid>
      )}

      <Grid
        size={{
          xs: gridSizeValue,
        }}
        sx={{
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          overflowY: 'auto',
          ...item.sx,
        }}
      >
        {displayValue()}
      </Grid>
    </>
  );
}
